import type { ZetaError, RunResult } from './types';

declare global {
  interface Window {
    cheerpjInit: (options: { version: number; status?: string }) => Promise<void>;
    cheerpOSAddStringFile: (path: string, content: string | Uint8Array) => void;
    cheerpjRunJar: (jarPath: string, ...args: string[]) => Promise<number>;
    cheerpjGetDisplay: () => HTMLElement | null;
  }
}

let cheerpjReady = false;
let loadPromise: Promise<void> | null = null;

function loadCheerpJScript(): Promise<void> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (document.querySelector('script[src*="cjrtnc.leaningtech.com"]')) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cjrtnc.leaningtech.com/4.3/loader.js';
    script.async = true;
    script.onload = () => {
      resolve();
    };
    script.onerror = () => reject(new Error('Failed to load CheerpJ'));
    document.head.appendChild(script);
  });

  return loadPromise;
}

export async function initCheerpJ(): Promise<void> {
  if (cheerpjReady) return;

  await loadCheerpJScript();
  await window.cheerpjInit({ version: 17, status: 'none' });
  cheerpjReady = true;
}

export function isCheerpJReady(): boolean {
  return cheerpjReady;
}

export function parseErrors(stderr: string): ZetaError[] {
  const errors: ZetaError[] = [];
  const lines = stderr.split('\n');

  for (const line of lines) {
    const match = line.match(/^Line\s+(\d+):\s+(.+)$/);
    if (match) {
      errors.push({
        line: parseInt(match[1], 10),
        column: 1,
        message: match[2],
        severity: 'error',
      });
    }
  }

  return errors;
}

export async function runZeta(source: string, stdin: string = ''): Promise<RunResult> {
  if (!cheerpjReady) {
    throw new Error('CheerpJ not initialized. Call initCheerpJ() first.');
  }

  const stdout: string[] = [];
  const stderr: string[] = [];

  const originalLog = console.log;
  const originalError = console.error;

  console.log = (...args: unknown[]) => {
    stdout.push(args.map(String).join(' '));
  };
  console.error = (...args: unknown[]) => {
    stderr.push(args.map(String).join(' '));
  };

  try {
    window.cheerpOSAddStringFile('/str/program.zeta', source);
    // Stdin is passed as a second argument; ZetaCompiler redirects System.in
    // from this file so `ask` statements read the playground's input panel.
    window.cheerpOSAddStringFile('/str/stdin.txt', stdin);
    const exitCode = await window.cheerpjRunJar('/app/zeta.jar', '/str/program.zeta', '/str/stdin.txt');

    const stdoutStr = stdout.join('\n');
    const stderrStr = stderr.join('\n');
    const errors = parseErrors(stderrStr);

    return {
      stdout: stdoutStr,
      stderr: stderrStr,
      errors,
      exitCode,
    };
  } finally {
    console.log = originalLog;
    console.error = originalError;
  }
}
